export function escapeMailComposeHtml(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
}

export function mailComposeTextToHtml(value: string): string | null {
  if (!value.trim()) return null;
  return value
    .replace(/\r\n?/gu, '\n')
    .split(/\n{2,}/gu)
    .map(paragraph => {
      const html = escapeMailComposeHtml(paragraph).replace(/\n/gu, '<br>');
      return `<p>${html || '<br>'}</p>`;
    })
    .join('');
}

export interface MailComposeParts {
  /** What the user typed into the composer. */
  userText: string;
  signatureText?: string | null;
  signatureHtml?: string | null;
  /** Quoted reply or forwarded message below the signature. */
  quoteText?: string | null;
  quoteHtml?: string | null;
}

const MAIL_COMPOSE_HTML_SEPARATOR = '<br><br>';

/** HTML of one composed part; plain-text parts are converted like typed text. */
export function mailComposePartHtml(
  html: string | null | undefined,
  text: string | null | undefined
): string | null {
  if (html?.trim()) return html;
  return text?.trim() ? mailComposeTextToHtml(text) : null;
}

/**
 * HTML the composer shows below the typed text: signature, then quote. It is
 * the same markup that `composeMailBody` sends, so the preview is exact.
 */
export function composeMailAppendixHtml(
  parts: Omit<MailComposeParts, 'userText'>
): string | null {
  const html = [
    mailComposePartHtml(parts.signatureHtml, parts.signatureText),
    mailComposePartHtml(parts.quoteHtml, parts.quoteText),
  ].filter((value): value is string => Boolean(value));
  return html.length ? html.join(MAIL_COMPOSE_HTML_SEPARATOR) : null;
}

/** Final text and HTML body of an outgoing mail. */
export function composeMailBody(parts: MailComposeParts): {
  bodyText: string;
  bodyHtml: string | null;
} {
  const bodyText = [parts.userText, parts.signatureText, parts.quoteText]
    .map(value => value?.replace(/\s+$/u, '') ?? '')
    .filter(value => value.trim())
    .join('\n\n');
  const html = [
    mailComposeTextToHtml(parts.userText),
    composeMailAppendixHtml(parts),
  ].filter((value): value is string => Boolean(value));
  return {
    bodyText,
    bodyHtml: html.length ? html.join(MAIL_COMPOSE_HTML_SEPARATOR) : null,
  };
}
